/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { CommonModule, DecimalPipe } from "@angular/common";
import {
    AfterViewInit,
    booleanAttribute,
    Component,
    computed,
    effect,
    ElementRef,
    input,
    model,
    OnInit,
    output,
    viewChild,
} from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { FormErrorDirective } from "../../directives/form-error.directive";
import { InputNumberDirective } from "../../directives/input-number.directive";
import { Hint } from "../hint/hint.component";
import { Icon } from "../icon/icon.component";
import { Label } from "../label/label.component";
import { BaseInput } from "./base-input";
import { debounceTime } from "rxjs";

@Component({
    selector: "app-input",
    host: {
        ngSkipHydration: "true",
    },
    standalone: true,
    imports: [Icon, Label, CommonModule, Hint, InputNumberDirective],
    styleUrl: "./input.component.css",
    templateUrl: "./input.component.html",
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: Input,
            multi: true,
        },
    ],
    hostDirectives: [FormErrorDirective],
})
export class Input
    extends BaseInput<any>
    implements AfterViewInit, OnInit, ControlValueAccessor
{
    inputClick = output<void>();
    inputFocus = output<void>();
    inputBlur = output<void>();
    inputKeydown = output<KeyboardEvent>();
    inputRef = viewChild<ElementRef<HTMLInputElement>>("inputRef");
    inputValue = model<string>();

    suffixClick = output<void>();

    prefix = input<string>("");
    suffix = input<string>();
    prefixIcon = input<string>();
    suffixIcon = input<string>();
    icon = input<string>();
    list = input<string>();

    inputNumber = input<boolean, string>(false, {
        transform: booleanAttribute,
    });
    max = input<number>();
    min = input<number>();
    max$ = toObservable(this.max);
    currentMax = computed<any>(() => {
        if (this.type() === "date") {
            return "9999-12-31";
        }

        return this.max();
    });

    wrap = input<boolean, string>(false, {
        transform: booleanAttribute,
    });

    _placeholder = computed(() => {
        return this.placeholder() || (this.label() ? ` ${this.label()} ` : "");
    });

    async ngAfterViewInit() {
        // 초기화
        setTimeout(() => {
            if (this.value()) {
                this.inputRef()!.nativeElement.value = this.value() || "";
            }
        }, 500);

        // 값 변경 감지
        this.value$.subscribe((value) => {
            if (value) {
                if (this.inputNumber() && !isNaN(Number(value))) {
                    const pipe = new DecimalPipe("ko-KR");
                    this.inputRef()!.nativeElement.value =
                        pipe.transform(value) || "";
                    return;
                }
                this.inputRef()!.nativeElement.value = value.toString() || "";
            }
        });

        // 디바운스 함수 정의
        const debounce = (func: (...args: any[]) => void, delay: number) => {
            let timeoutId: ReturnType<typeof setTimeout>;
            return (...args: any[]) => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => func(...args), delay);
            };
        };

        // 입력 처리 함수
        const handleInput = (event: Event) => {
            const inputElement = event.target as HTMLInputElement;
            let inputValue = inputElement.value;

            if (this.inputMode() === "numeric") {
                inputValue = inputValue.replace(/\D/g, ""); // 숫자만 남기기
                inputElement.value = inputValue; // UI 업데이트
            }

            // inputNumber가 true일 때 숫자, -, ., 콤마만 허용
            if (this.inputNumber()) {
                inputValue = inputValue.replace(/[^0-9.\-,]/g, "");

                // max 값이 설정되어 있고, inputNumber 모드일 때 max 값을 초과하면 max로 제한
                if (this.max() !== undefined && this.max() !== null) {
                    // 콤마 제거 후 숫자로 변환
                    const numericValue = Number(inputValue.replace(/,/g, ""));

                    if (!isNaN(numericValue) && numericValue > this.max()!) {
                        // max 값을 초과하면 max 값으로 제한
                        inputValue = this.max()!.toString();
                    }
                }

                // min 값이 설정되어 있고, inputNumber 모드일 때 min 값보다 작으면 min으로 제한
                if (this.min() !== undefined && this.min() !== null) {
                    // 콤마 제거 후 숫자로 변환
                    const numericValue = Number(inputValue.replace(/,/g, ""));

                    if (!isNaN(numericValue) && numericValue < this.min()!) {
                        // min 값보다 작으면 min 값으로 제한
                        inputValue = this.min()!.toString();
                    }
                }

                inputElement.value = inputValue; // UI 업데이트
            }

            this.value.set(inputValue); // 상태 업데이트
        };

        // 디바운스 적용
        const debouncedInputHandler = debounce(handleInput, this.debounce()!); // 300ms 디바운스

        // 네이티브 oninput에 디바운스 함수 연결
        this.inputRef()!.nativeElement.oninput = debouncedInputHandler;
    }

    /**
     * @description input element의 change 이벤트 발생 시 호출되는 함수
     * (type이 date인 경우에만 호출)
     */
    handleChange() {
        if (this.type() === "date") {
            this.value.set(this.inputRef()!.nativeElement.value);
        }
    }
}
